import { motion } from "framer-motion";
import { Sparkles, Heart, Briefcase, Crown } from "lucide-react";
import HeroScene from "./HeroScene";
import { FiligreeFlourish, DividerStar } from "./Filigree";

const trustItems = [
  { icon: Heart, label: "Weddings" },
  { icon: Sparkles, label: "Festive & Garba" },
  { icon: Briefcase, label: "Corporate" },
  { icon: Crown, label: "Receptions" },
];

export default function Hero() {
  return (
    <section id="top" className="relative min-h-screen w-full overflow-hidden" data-testid="hero-section">
      {/* 3D Scene */}
      <div className="absolute inset-0 z-0">
        <HeroScene />
      </div>

      {/* Vignette + warm Indian gradients (gold + maroon glow) */}
      <div className="absolute inset-0 z-[1] vignette pointer-events-none" />
      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 20% 30%, rgba(212, 175, 55, 0.16), transparent 50%), radial-gradient(ellipse at 80% 70%, rgba(150, 40, 50, 0.12), transparent 55%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 min-h-screen flex flex-col justify-center pt-28 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="flex items-center gap-3 mb-6"
          data-testid="hero-eyebrow"
        >
          <span className="h-px w-10 bg-gold" />
          <span className="text-[10px] sm:text-xs md:text-sm uppercase tracking-[0.2em] sm:tracking-[0.3em] text-gold/90 font-medium whitespace-nowrap">
            Vadodara, Gujarat · Founder-led
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.3 }}
          className="max-w-3xl"
        >
          <FiligreeFlourish className="w-72 md:w-96 h-12 opacity-70 mb-3" />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="font-serif font-light text-5xl sm:text-6xl lg:text-[88px] leading-[1.02] tracking-tight max-w-5xl"
          data-testid="hero-heading"
        >
          Bring your dream
          <br />
          <span className="text-gold-gradient italic">celebration</span>{" "}
          <span className="text-ivory-gradient">to life.</span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.5 }}
        >
          <FiligreeFlourish flip className="w-72 md:w-96 h-12 opacity-70 mt-3" />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.65 }}
          className="mt-8 max-w-2xl text-base md:text-lg text-ivory/75 leading-relaxed font-light"
          data-testid="hero-subheading"
        >
          Founder-led event design and planning for weddings, receptions, festive nights,
          corporate gatherings and private celebrations — crafted with creativity, organised
          beautifully, and delivered with warmth.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center"
        >
          <a href="#contact" className="btn-gold" data-testid="hero-primary-cta">
            Plan Your Event
          </a>
          <a href="#services" className="btn-ghost-gold" data-testid="hero-secondary-cta">
            Explore Services →
          </a>
        </motion.div>

        {/* Trust pills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.05 }}
          className="mt-16 md:mt-20 flex flex-wrap items-center gap-3 md:gap-4"
          data-testid="hero-trust-pills"
        >
          {trustItems.map((t, i) => {
            const Icon = t.icon;
            return (
              <div
                key={t.label}
                className="glass rounded-full px-4 py-2 flex items-center gap-2 hover:border-gold/40 transition-all duration-300"
                data-testid={`trust-pill-${i}`}
              >
                <Icon size={14} className="text-gold" />
                <span className="text-xs md:text-sm text-ivory/85 tracking-wide">{t.label}</span>
              </div>
            );
          })}
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] text-ivory/40">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-px h-12 bg-gradient-to-b from-gold/70 to-transparent"
          />
        </motion.div>
      </div>
    </section>
  );
}
